import { useQuery, useQueryClient } from "@tanstack/react-query";
import { transactionApi } from "@bw/shared/api";
import {
  capturePaymentMethodDetachBarrierGeneration,
  paymentQueryKeys,
  reconcilePaymentMethods,
} from "@bw/shared/hooks";
import type {
  SavedPaymentMethodResponse,
  StripeFrontendConfigResponse,
} from "@bw/shared/types/generated";

/**
 * Facility-scoped Stripe Connect payment spine for enrollment, reusing the SAME
 * transactionApi endpoints booking uses. Requests remain keyed by facilityId,
 * while detach safety follows the connected account returned by the backend
 * because sibling facilities can share one organization wallet.
 *
 * Keyed via the SHARED `paymentQueryKeys` so a card saved during a booking is the
 * same facility-routed cache slot as one listed here.
 *
 * TODO(payment-hook-dedup): these classes wrappers do NOT adopt the shared
 * @bw/shared/hooks payment hooks (useFrontendConfig / usePaymentMethods /
 * useWalletSetupIntent) because the classes claim flow genuinely differs: the shared
 * hooks gate on `Boolean(facilityId) && enabled`, but the CLAIM surface
 * (ClaimSection) learns its facility from the enrollment response and may flip
 * `needsPayment` true BEFORE a truthy facility_id is in hand — gating on
 * facilityId would race (skip) the payment-methods fetch and strand a paid claim
 * with no card. So these classes wrappers gate on `enabled` ALONE (no facilityId
 * guard) — the deliberate behavioral difference. Booking + tournament (which
 * always have a facility by the time payment is needed) DID adopt the shared
 * hooks. If the claim flow is reworked to guarantee a facility before payment, or
 * the shared hook grows an explicit "skip the facilityId guard" option, fold these
 * three in. See Claim.security.test.tsx (the characterization net that pins this).
 */
export function useClassPaymentMethods(
  facilityId: string | undefined,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const queryKey = paymentQueryKeys.paymentMethods(facilityId ?? "");
  // `enabled` is the caller's explicit "payment is required now" signal — it is
  // the authoritative gate (callers only flip it once a charge is actually
  // needed). We do NOT additionally require a truthy facilityId: the facility may
  // arrive on the same response that triggers payment, and gating on it would
  // race the fetch. The id is still the cache key + the request scope.
  return useQuery<SavedPaymentMethodResponse[]>({
    queryKey,
    queryFn: async () => {
      const authGeneration = capturePaymentMethodDetachBarrierGeneration();
      const methods = await transactionApi.listPaymentMethods(
        facilityId as string,
      );
      return reconcilePaymentMethods(
        queryClient,
        queryKey,
        methods,
        authGeneration,
      );
    },
    enabled,
    retry: false,
  });
}

export function useClassFrontendConfig(
  facilityId: string | undefined,
  enabled = true,
) {
  return useQuery<StripeFrontendConfigResponse>({
    queryKey: paymentQueryKeys.frontendConfig(facilityId ?? ""),
    queryFn: () => transactionApi.getFrontendConfig(facilityId as string),
    enabled,
    retry: false,
  });
}

export { useWalletSetupIntent as useClassSetupIntent } from "@bw/shared/hooks";
